import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import { FaChevronDown } from 'react-icons/fa6';
import { ArrowLeft } from 'lucide-react';
import Heading from 'components/Heading';
import { FSBTable } from 'components/common/FSBTable';
import { showSequenceNumber } from 'utils/constants';
import { authContext } from 'contexts/AuthContext';
import TemplateDetailsModal from './TemplateDetailsModal';

const WHATSAPP_API_BASE = 'https://graph.facebook.com/v18.0/170213176168132';
const REST_API = process.env.REACT_APP_REST_API_ENDPOINT;
const REST_API_ROOT = REST_API?.replace(/\/api\/?$/, '/');
const AUTH_TOKEN =
  'Bearer EAADpi91hNyMBO8ZCm4wy5B4XWs5mDDSXZBm0HhIm2m623j5rJASVWtPK4sCpbflcC4B7DaQTgjbPDwqDTTspXm1U1kGdGIXV0ZBZCQZAajvBxpfsEbi1OruYzMk51s0LaluMMj8LfZAR10v3k1BCbbi8laDIYK3QNVfwda5Y6IRx4gDTSZAOzUQyQZBSSkvCcZC0UWDU2SMzd3wplZCd5E';

const WhatsAppAnalytics = ({ dateRange }) => {
  const { auth } = useContext(authContext);
  const [templates, setTemplates] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedMobileCardId, setExpandedMobileCardId] = useState(null);
  const [selectedRecipientsTemplate, setSelectedRecipientsTemplate] = useState(null);
  const [recipients, setRecipients] = useState({ data: [], totalCount: 0, totalPages: 0 });
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const lastRequestRef = useRef({ startUnix: null, endUnix: null });
  const loadingRef = useRef(false);
  const sortBySentDesc = (data) =>
    [...data].sort((a, b) => {
      const diff = (b.count || 0) - (a.count || 0);
      if (diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '');
    });

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${WHATSAPP_API_BASE}/message_templates`,
        {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        },
      );
      return response.data.data;
    } catch (err) {
      console.error('Error fetching templates:', err);
      throw err;
    }
  };

  // Fetch analytics for a specific template
  const fetchTemplateAnalytics = async (templateId, start, end) => {
    try {
      const response = await axios.get(
        `${WHATSAPP_API_BASE}/template_analytics`,
        {
          params: {
            start,
            end,
            granularity: 'daily',
            metric_types: 'cost,clicked,delivered,read,sent',
            template_ids: `[${templateId}]`,
          },
          headers: {
            Authorization: AUTH_TOKEN,
          },
        },
      );
      return response.data.data[0]?.data_points || [];
    } catch (err) {
      console.error('Error fetching analytics:', err);
      return [];
    }
  };

  // Build template text from components
  const buildTemplateText = (template) => {
    let text = '';

    if (template.components) {
      const bodyComponent = template.components.find((c) => c.type === 'BODY');
      if (bodyComponent) {
        text = bodyComponent.text;

        // Replace placeholders with example values if available
        if (bodyComponent.example && bodyComponent.example.body_text) {
          const exampleValues = bodyComponent.example.body_text[0];
          exampleValues.forEach((value, index) => {
            text = text.replace(`{{${index + 1}}}`, value);
          });
        }
      }
    }

    return text || template.name;
  };

  // Load all data
  const loadData = useCallback(async (startDate = null, endDate = null) => {
    const startUnix = startDate
      ? moment(startDate).startOf('day').unix()
      : moment().subtract(30, 'days').unix();
    const endUnix = endDate
      ? moment(endDate).endOf('day').unix()
      : moment().unix();

    if (
      loadingRef.current &&
      lastRequestRef.current.startUnix === startUnix &&
      lastRequestRef.current.endUnix === endUnix
    ) {
      return;
    }

    if (
      lastRequestRef.current.startUnix === startUnix &&
      lastRequestRef.current.endUnix === endUnix
    ) {
      return;
    }

    lastRequestRef.current = { startUnix, endUnix };
    setLoading(true);
    loadingRef.current = true;
    setError(null);

    try {
      const templatesData = await fetchTemplates();
      setTemplates(templatesData);

      const analyticsPromises = templatesData.map(async (template) => {
        const analytics = await fetchTemplateAnalytics(
          template.id,
          startUnix,
          endUnix,
        );

        const totalMetrics = analytics.reduce((acc, point) => {
          return {
            sent: (acc.sent || 0) + (point.sent || 0),
            delivered: (acc.delivered || 0) + (point.delivered || 0),
            read: (acc.read || 0) + (point.read || 0),
            clicked: (acc.clicked || 0) + (point.clicked || 0),
            totalCost:
              (acc.totalCost || 0) +
              (point.cost?.find((c) => c.type === 'amount_spent')?.value || 0),
          };
        }, {});

        return {
          id: template.id,
          name: template.name,
          title: buildTemplateText(template),
          status: template.status,
          category: template.category,
          language: template.language,
          components: template.components,
          count: totalMetrics.sent || 0,
          delivered: totalMetrics.delivered || 0,
          read: totalMetrics.read || 0,
          clicked: totalMetrics.clicked || 0,
          totalCost: totalMetrics.totalCost || 0,
          analytics,
          lastUpdate:
            analytics.length > 0 ? analytics[analytics.length - 1].end : null,
        };
      });

      const analyticsResults = await Promise.all(analyticsPromises);
      const sortedResults = sortBySentDesc(analyticsResults);
      setAnalyticsData(sortedResults);
      setFilteredData(sortedResults);
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadData(dateRange?.startDate || null, dateRange?.endDate || null);
  }, [loadData, dateRange?.startDate, dateRange?.endDate]);

  const handleViewDetails = (template) => {
    setPageIndex(0);
    setSelectedRecipientsTemplate(template);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
  };

  const fetchRecipients = useCallback(async (template, requestedPage, requestedPageSize) => {
    if (!template) return;
    setRecipientsLoading(true);
    setRecipientsError('');
    try {
      const response = await axios.get(`${REST_API_ROOT}whatsapp-insights-users`, {
        params: { templateName: template.name, page: requestedPage, size: requestedPageSize },
        headers: auth?.data?.token ? { Authorization: `Bearer ${auth.data.token}` } : {},
      });
      const payload = response?.data?.data ?? response?.data;
      const rows = Array.isArray(payload) ? payload : (payload?.content || []);
      const totalCount = response?.data?.totalCount
        ?? response?.data?.totalElements
        ?? payload?.totalCount
        ?? payload?.totalElements
        ?? rows.length;
      const totalPages = response?.data?.totalPages
        ?? payload?.totalPages
        ?? Math.ceil(Number(totalCount) / requestedPageSize);
      setRecipients({ data: rows, totalCount: Number(totalCount), totalPages: Number(totalPages) });
    } catch (err) {
      setRecipientsError(err?.response?.data?.message || err?.message || 'Unable to load WhatsApp recipients.');
      setRecipients({ data: [], totalCount: 0, totalPages: 0 });
    } finally {
      setRecipientsLoading(false);
    }
  }, [auth?.data?.token]);

  useEffect(() => {
    if (selectedRecipientsTemplate) fetchRecipients(selectedRecipientsTemplate, pageIndex, pageSize);
  }, [selectedRecipientsTemplate, pageIndex, pageSize, fetchRecipients]);

  const recipientColumns = useMemo(() => [
    { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false, cell: (cell) => showSequenceNumber(cell, { pageNo: pageIndex + 1, pageSize }) },
    { accessorKey: 'userId', header: 'ID', enableSorting: false },
    { accessorKey: 'userName', header: 'Name', enableSorting: false },
    { accessorKey: 'phone', header: 'Number', enableSorting: false },
    { accessorKey: 'sentCount', header: 'Sent', enableSorting: false },
    { accessorKey: 'deliveredCount', header: 'Delivered', enableSorting: false },
    { accessorKey: 'readCount', header: 'Read', enableSorting: false },
    { accessorKey: 'clickedCount', header: 'Clicked', enableSorting: false },
  ], [pageIndex, pageSize]);

  const handleRecipientPageChange = ({ pageNo: nextPage, pageSize: nextPageSize }) => {
    setPageSize(nextPageSize);
    setPageIndex(nextPageSize === pageSize ? nextPage - 1 : 0);
  };

  if (selectedRecipientsTemplate) {
    return (
      <div className="push-notification-container">
        <div className="daily-price-sharing__header whatsapp-message-detail-header">
          <div className="whatsapp-message-detail-header__content">
            <button type="button" className="push-notification__back" onClick={() => setSelectedRecipientsTemplate(null)}>
              <ArrowLeft size={18} /> Back
            </button>
            <div className="whatsapp-message-detail-header__title">
              <Heading title={selectedRecipientsTemplate.name} />
            </div>
          </div>
        </div>
        {recipientsError ? <div className="daily-price-sharing__error" role="alert">{recipientsError}</div> : null}
        <FSBTable
          data={recipients.data}
          columns={recipientColumns}
          totalCount={recipients.totalCount}
          totalPages={recipients.totalPages}
          currentPageNo={pageIndex + 1}
          pageSize={pageSize}
          keyField="userId"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          loading={recipientsLoading}
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break push-notification-user-table"
          tableWraperClass="whatsapp-insights-table-wrap"
          handlePageChange={handleRecipientPageChange}
        />
      </div>
    );
  }

  const headerStyle = {
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '13px',
    color: '#495057',
    borderBottom: '2px solid #dee2e6',
    background: '#f1f3f5',
  };

  const cellStyle = {
    padding: '12px 15px',
    verticalAlign: 'middle',
    color: '#495057',
    fontSize: '13px',
  };

  // Loading state with Bootstrap spinner
  if (loading) {
    return (
      <div
        className="p-4"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '50vh' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              Loading WhatsApp analytics...
            </span>
          </div>
          <span className="ms-3 text-muted">Loading WhatsApp analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="whatsapp-analytics"
      style={{
        width: '100%',
        height: '100%',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      {error && (
        <div
          style={{
            padding: '15px',
            background: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            margin: '15px',
            borderRadius: '4px',
          }}
          role="alert"
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '15px' }}>
        {filteredData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No data available for the selected date range
          </div>
        ) : (
          <>
            <div className="analytics-mobile-list whatsapp-mobile-list">
              {filteredData.map((item, index) => {
                const isExpanded = expandedMobileCardId === item.id;
                return (
                  <div
                    className="analytics-mobile-card"
                    key={`mobile-${item.id}`}
                  >
                    <div className="analytics-mobile-card__summary">
                      <div className="analytics-mobile-card__identity">
                        <div className="analytics-mobile-card__name">
                          {item.name}
                        </div>
                        <div className="analytics-mobile-card__meta">
                          {`S.No ${index + 1} | ${item.title || '-'}`}
                        </div>
                      </div>
                      <div className="analytics-mobile-card__aside">
                        <span
                          className={`analytics-mobile-card__pill ${
                            item.status === 'APPROVED' ? 'success' : 'warning'
                          }`}
                        >
                          {item.status}
                        </span>
                        <div className="analytics-mobile-card__tools">
                          <button
                            type="button"
                            className="analytics-mobile-card__icon-btn"
                            onClick={() => handleViewDetails(item)}
                            aria-label={`View details for template: ${item.name}`}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className={`analytics-mobile-card__expand-toggle ${
                              isExpanded ? 'is-open' : ''
                            }`}
                            onClick={() => {
                              setExpandedMobileCardId((prevId) => {
                                return prevId === item.id ? null : item.id;
                              });
                            }}
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? 'Collapse WhatsApp message details'
                                : 'Expand WhatsApp message details'
                            }
                          >
                            <FaChevronDown />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="analytics-mobile-card__detail-list">
                        <div className="analytics-mobile-card__detail-row">
                          <span className="analytics-mobile-card__detail-label">
                            Sent
                          </span>
                          <span className="analytics-mobile-card__detail-value">
                            {item.count.toLocaleString()}
                          </span>
                        </div>
                        <div className="analytics-mobile-card__detail-row">
                          <span className="analytics-mobile-card__detail-label">
                            Delivered
                          </span>
                          <span className="analytics-mobile-card__detail-value">
                            {item.delivered.toLocaleString()}
                          </span>
                        </div>
                        <div className="analytics-mobile-card__detail-row">
                          <span className="analytics-mobile-card__detail-label">
                            Read
                          </span>
                          <span className="analytics-mobile-card__detail-value">
                            {item.read.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div
              className="analytics-desktop-table"
              style={{ overflowX: 'auto' }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  tableLayout: 'fixed',
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...headerStyle, width: '60px' }} scope="col">
                      S.No.
                    </th>
                    <th style={{ ...headerStyle, width: '200px' }} scope="col">
                      Template Name
                    </th>
                    <th style={{ ...headerStyle, width: 'auto' }} scope="col">
                      Title/Message
                    </th>
                    <th style={{ ...headerStyle, width: '120px' }} scope="col">
                      Status
                    </th>
                    <th
                      style={{
                        ...headerStyle,
                        width: '80px',
                        textAlign: 'center',
                      }}
                      scope="col"
                    >
                      Sent
                    </th>
                    <th
                      style={{
                        ...headerStyle,
                        width: '100px',
                        textAlign: 'center',
                      }}
                      scope="col"
                    >
                      Delivered
                    </th>
                    <th
                      style={{
                        ...headerStyle,
                        width: '80px',
                        textAlign: 'center',
                      }}
                      scope="col"
                    >
                      Read
                    </th>
                    <th
                      style={{
                        ...headerStyle,
                        width: '80px',
                        textAlign: 'center',
                      }}
                      scope="col"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: '1px solid #e9ecef',
                        background: index % 2 === 0 ? '#fff' : '#fafafa',
                      }}
                    >
                      <td style={cellStyle}>{index + 1}</td>
                      <td style={{ ...cellStyle, fontWeight: 500 }}>
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.name}
                        >
                          <button
                            type="button"
                            onClick={() => handleViewDetails(item)}
                            style={{ border: 0, padding: 0, background: 'transparent', color: '#1565c0', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                          >
                            {item.name}
                          </button>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.title}
                        >
                          {item.title}
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background:
                              item.status === 'APPROVED'
                                ? '#d4edda'
                                : '#fff3cd',
                            color:
                              item.status === 'APPROVED'
                                ? '#155724'
                                : '#856404',
                            fontSize: '11px',
                            fontWeight: 600,
                          }}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            background: '#e3f2fd',
                            color: '#1565c0',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          {item.count.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            background: '#e8f5e9',
                            color: '#2e7d32',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          {item.delivered.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            background: '#fff3e0',
                            color: '#ef6c00',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          {item.read.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleViewDetails(item)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '8px',
                            cursor: 'pointer',
                            color: '#495057',
                            lineHeight: 1,
                            borderRadius: '4px',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleViewDetails(item);
                            }
                          }}
                          aria-label={`View details for template: ${item.name}`}
                          title={`View details for ${item.name}`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          borderTop: '2px solid #e0e0e0',
          padding: '15px 20px',
          background: '#f8f9fa',
          fontSize: '13px',
          fontWeight: 500,
          color: '#495057',
        }}
      >
        <span>Total Templates: {filteredData.length}</span>
        <span style={{ marginLeft: '20px' }}>
          Total Messages Sent:{' '}
          {filteredData
            .reduce((sum, item) => sum + (item.count || 0), 0)
            .toLocaleString()}
        </span>
        <span style={{ marginLeft: '20px' }}>
          Total Cost: ₹
          {filteredData
            .reduce((sum, item) => sum + (item.totalCost || 0), 0)
            .toFixed(2)}
        </span>
      </div>

      {/* Template Details Modal */}
      {showModal && (
        <TemplateDetailsModal
          template={selectedTemplate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

WhatsAppAnalytics.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
};

export default WhatsAppAnalytics;
